<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import {
    PRICE_INPUT_MAX_LENGTH,
    SIZES_MAX,
    SIZE_LABEL_MAX,
    addSizeRow,
    removeSizeRow,
    startSizes,
    updateSizeRow,
  } from '@/domain/menu-import/item-editor';
  import type {
    IPriceSizesErrors,
    IPriceSizesForm,
    ISizeRowErrors,
    ISizeRowForm,
  } from '@/domain/menu-import/item-editor';
  import { currencySymbol } from '@/domain/store-setup/money';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Precio» or «Tamaños» of a dish (plan §4.5.1): the single price, or 2 to
   * 20 sizes with a name and a price. The first «Agregar tamaño» turns the
   * price into two sizes (the current price and an empty one); removing down
   * to one size turns it back into a plain price. Controlled: the parent owns
   * the form and decides when to save (the editor when the focus leaves this
   * block, the review queue on «Guardar cambio»). `fixedLabels` (a dish
   * already on the menu): only the prices change, the sizes stay as they
   * are, each price with its hint («Hoy: $ 9.000»).
   */
  const props = withDefaults(
    defineProps<{
      value: IPriceSizesForm;
      /** Shown after a save attempt. */
      errors?: IPriceSizesErrors | null;
      /** The prices' currency (its symbol prefixes the inputs). */
      currency: string;
      /** Size names read-only, no sizes added or removed. */
      fixedLabels?: boolean;
      /** Under the single price. */
      priceHint?: string | null;
      /** Under each size's price, by row index. */
      hints?: (string | null)[];
    }>(),
    { errors: null, fixedLabels: false, priceHint: null, hints: () => [] }
  );
  const emit = defineEmits<{
    (e: 'input', value: IPriceSizesForm): void;
    /** The focus entered (true) or left (false) this block. */
    (e: 'focus-change', inside: boolean): void;
  }>();

  const uid = nextDomId('menu-sizes');
  const singlePriceId = `${uid}-price`;
  const singlePriceHintId = `${uid}-price-hint`;
  const titleId = `${uid}-title`;
  const root = ref<HTMLElement | null>(null);
  let alive = true;

  const symbol = computed(() => currencySymbol(props.currency));
  const sizesMode = computed(() => props.value.rows.length > 0);
  const canAdd = computed(
    () => !props.fixedLabels && props.value.rows.length < SIZES_MAX
  );

  function labelId(key: string): string {
    return `${uid}-${key}-label`;
  }

  function priceId(key: string): string {
    return `${uid}-${key}-price`;
  }

  function hintId(key: string): string {
    return `${uid}-${key}-hint`;
  }

  function rowHint(index: number): string | null {
    return props.hints[index] ?? null;
  }

  function rowError(key: string, field: keyof ISizeRowErrors): boolean {
    return props.errors?.rows[key]?.[field] ?? false;
  }

  function focusById(id: string) {
    const element = document.getElementById(id);
    if (element instanceof HTMLInputElement) element.focus();
  }

  /** After the rows change: the focus goes to `id` (the previous focus target may be gone). */
  async function focusAfterRender(id: string) {
    await nextTick();
    if (alive) focusById(id);
  }

  function setPrice(text: string) {
    emit('input', { ...props.value, priceText: text });
  }

  /** Enter in the price: done with it (the parent saves when the focus leaves). */
  function leavePrice() {
    const input = document.getElementById(singlePriceId);
    if (input instanceof HTMLElement) input.blur();
  }

  function setRow(key: string, patch: Partial<ISizeRowForm>) {
    emit('input', updateSizeRow(props.value, key, patch));
  }

  function start() {
    const next = startSizes(props.value, [
      nextDomId('menu-size'),
      nextDomId('menu-size'),
    ]);
    emit('input', next);
    void focusAfterRender(labelId(next.rows[0].key));
  }

  function add() {
    const key = nextDomId('menu-size');
    emit('input', addSizeRow(props.value, key));
    void focusAfterRender(labelId(key));
  }

  function remove(key: string) {
    const index = props.value.rows.findIndex((row) => row.key === key);
    const next = removeSizeRow(props.value, key);
    emit('input', next);
    const neighbour = next.rows[Math.min(index, next.rows.length - 1)] ?? null;
    void focusAfterRender(neighbour ? labelId(neighbour.key) : singlePriceId);
  }

  function onFocusIn() {
    emit('focus-change', true);
  }

  /** Moving between the fields and buttons of this block is not leaving it. */
  function onFocusOut(event: FocusEvent) {
    const next = event.relatedTarget;
    if (next instanceof Node && root.value?.contains(next)) return;
    emit('focus-change', false);
  }

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div
    ref="root"
    class="v2-menu-sizes-editor"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <template v-if="!sizesMode">
      <PosUiField
        :label="$t('menuImport.editor.price')"
        :input-id="singlePriceId"
        :hint="priceHint || undefined"
        :hint-id="singlePriceHintId"
        :error="
          errors && errors.price
            ? $t('menuImport.editor.priceInvalid')
            : undefined
        "
      >
        <PosUiInput
          :id="singlePriceId"
          :aria-describedby="priceHint ? singlePriceHintId : undefined"
          :value="value.priceText"
          :prefix="symbol"
          :maxlength="PRICE_INPUT_MAX_LENGTH"
          :placeholder="$t('menuImport.item.pricePlaceholder')"
          inputmode="decimal"
          autocomplete="off"
          @input="setPrice"
          @enter="leavePrice"
        />
      </PosUiField>
      <button
        v-if="!fixedLabels"
        type="button"
        class="sizes-link"
        @click="start"
      >
        <PosUiIcon name="plus" class="link-icon" />
        {{ $t('menuImport.editor.addSize') }}
      </button>
    </template>
    <div v-else class="sizes" role="group" :aria-labelledby="titleId">
      <p :id="titleId" class="sizes-title">
        {{ $t('menuImport.editor.sizes') }}
      </p>
      <div v-for="(row, index) in value.rows" :key="row.key" class="size-row">
        <div class="size-inputs">
          <label
            v-if="fixedLabels"
            :id="labelId(row.key)"
            class="size-name"
            :for="priceId(row.key)"
            >{{ row.label }}</label
          >
          <PosUiInput
            v-else
            :id="labelId(row.key)"
            class="size-label"
            :value="row.label"
            :maxlength="SIZE_LABEL_MAX"
            :placeholder="$t('menuImport.editor.sizeLabel')"
            :aria-label="$t('menuImport.editor.sizeLabel')"
            autocomplete="off"
            @input="setRow(row.key, { label: $event })"
          />
          <PosUiInput
            :id="priceId(row.key)"
            class="size-price"
            :value="row.priceText"
            :prefix="symbol"
            :maxlength="PRICE_INPUT_MAX_LENGTH"
            :placeholder="$t('menuImport.editor.sizePrice')"
            :aria-label="
              fixedLabels ? undefined : $t('menuImport.editor.sizePrice')
            "
            :aria-describedby="
              rowHint(index) !== null ? hintId(row.key) : undefined
            "
            inputmode="decimal"
            autocomplete="off"
            @input="setRow(row.key, { priceText: $event })"
          />
          <button
            v-if="!fixedLabels"
            type="button"
            class="size-remove"
            :aria-label="$t('menuImport.editor.removeSize')"
            :title="$t('menuImport.editor.removeSize')"
            @click="remove(row.key)"
          >
            <PosUiIcon name="close" :size="18" />
          </button>
        </div>
        <p
          v-if="rowHint(index) !== null"
          :id="hintId(row.key)"
          class="size-hint"
        >
          {{ rowHint(index) }}
        </p>
        <p v-if="rowError(row.key, 'label')" class="size-error" role="alert">
          {{ $t('menuImport.editor.nameRequired') }}
        </p>
        <p
          v-else-if="rowError(row.key, 'price')"
          class="size-error"
          role="alert"
        >
          {{ $t('menuImport.editor.priceInvalid') }}
        </p>
      </div>
      <button v-if="canAdd" type="button" class="sizes-link" @click="add">
        <PosUiIcon name="plus" class="link-icon" />
        {{ $t('menuImport.editor.addSize') }}
      </button>
      <p
        v-if="!fixedLabels"
        class="sizes-hint"
        :class="{ 'is-error': errors && errors.count }"
        :role="errors && errors.count ? 'alert' : undefined"
      >
        {{ $t('menuImport.editor.sizesRule') }}
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-sizes-editor {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-1;

    .sizes {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    }
    .sizes-title {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .size-row {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .size-inputs {
      @apply tw-flex tw-items-center tw-gap-2;
    }
    .size-label {
      @apply tw-min-w-0 tw-flex-1;
    }
    .size-name {
      @apply tw-min-w-0 tw-flex-1 tw-break-words tw-text-sm tw-text-neutral-900;
    }
    .size-hint {
      @apply tw-m-0 tw-text-right tw-text-xs tw-text-neutral-500;
    }
    .size-price {
      @apply tw-w-32 tw-shrink-0;
    }
    .size-remove {
      @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-bg-neutral-100 hover:tw-text-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .size-error {
      @apply tw-m-0 tw-text-xs tw-text-red-600;
    }
    .sizes-link {
      @apply tw-inline-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-1 tw-self-start tw-border-0 tw-bg-transparent tw-px-0 tw-text-sm tw-font-medium tw-text-neutral-700 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      .link-icon {
        @apply tw-text-base;
      }
    }
    .sizes-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
      &.is-error {
        @apply tw-text-red-600;
      }
    }
  }
</style>
